import React from 'react';

import { Icon, Text } from '../../../../components';
import { MAX_MASCOTS, MAX_MASCOT_IMAGES, type ArtStyleMascot } from '../../art-style-api';
import type { PendingImage } from '../../useArtStyle';
import { MascotCard } from './MascotCard';
import { AddButton, Cards, Count, Field, Head, Hint, Label, LabelRow } from './MascotsField.styles';

export type MascotsFieldProps = {
  /** The cast as the owner sees it (unsaved edits included). */
  mascots: readonly ArtStyleMascot[];
  /** Images still uploading (only the mascot ones are read). */
  pending: readonly PendingImage[];
  /** The mascot Lumio is looking at right now. */
  describingId: string | null;
  /** Per mascot id, bumps when Lumio's suggested name lands. */
  nameReveals: Readonly<Record<string, number>>;
  onAdd: () => void;
  onRemove: (mascotId: string) => void;
  onRename: (mascotId: string, name: string) => void;
  onSuggestName: (mascotId: string) => void;
  onAddFiles: (mascotId: string, files: File[]) => void;
  onRemoveImage: (mascotId: string, index: number) => void;
  /** Open one of a mascot's images full size. */
  onOpenImage?: (mascotId: string, index: number) => void;
  /** Leave out the title row (an accordion section titles and counts the cast). */
  headless?: boolean;
};

/**
 * The Ingredients column's Mascots section: the style's cast of the brand's own characters, one card per mascot
 * (see `MascotCard`), and "+ Add mascot" up to {@link MAX_MASCOTS}. Every change autosaves with the style (the
 * whole cast at once); Lumio names and describes each mascot once it has images.
 */
export const MascotsField = ({
  mascots,
  pending,
  describingId,
  nameReveals,
  onAdd,
  onRemove,
  onRename,
  onSuggestName,
  onAddFiles,
  onRemoveImage,
  onOpenImage,
  headless = false,
}: MascotsFieldProps): React.ReactElement => (
  <Field>
    <Head>
      {headless ? null : (
        <LabelRow>
          <Icon name="user" size={15} />
          <Label text="Characters" tag="span" />
          {mascots.length > 0 ? (
            <Count text="{{n}} of {{max}}" placeholders={{ n: mascots.length, max: MAX_MASCOTS }} tag="span" />
          ) : null}
        </LabelRow>
      )}
      <Hint
        text="Your brand's own characters. Give each one up to {{n}} images of the same character. Transparent PNG cut-outs work best."
        placeholders={{ n: MAX_MASCOT_IMAGES }}
        tag="span"
      />
    </Head>
    {mascots.length > 0 ? (
      <Cards>
        {mascots.map((mascot) => (
          <MascotCard
            key={mascot.id}
            mascot={mascot}
            pending={pending
              .filter((p) => p.kind === 'mascot' && p.mascotId === mascot.id)
              .map(({ key, dataUri }) => ({ key, dataUri }))}
            describing={describingId === mascot.id}
            nameReveal={nameReveals[mascot.id] ?? 0}
            onRename={(name) => onRename(mascot.id, name)}
            onSuggestName={() => onSuggestName(mascot.id)}
            onRemove={() => onRemove(mascot.id)}
            onAddFiles={(files) => onAddFiles(mascot.id, files)}
            onRemoveImage={(index) => onRemoveImage(mascot.id, index)}
            onOpenImage={onOpenImage ? (index) => onOpenImage(mascot.id, index) : undefined}
          />
        ))}
      </Cards>
    ) : null}
    {mascots.length < MAX_MASCOTS ? (
      <AddButton type="button" onClick={onAdd}>
        <Icon name="plus" size={14} />
        <Text text="Add character" tag="span" />
      </AddButton>
    ) : null}
  </Field>
);

export default MascotsField;
